/**
 * Instantiate the container.
 *
 * Objects and parameters can be passed as argument to the constructor.
 *
 * @license
 * This file is part of pimple.js.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is furnished
 * to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 *
 * @copyright 2009 Fabien Potencier
 * @copyright 2012 brainbits GmbH
 *
 * @class Pimple
 */
export default class Pimple {
    staticValues = {};
    values = {};

    /**
     * @param {Object} values The parameters or objects
     */
    constructor(values = {}) {
        this.values = values;
    }

    /**
     * Gets a parameter or an object.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @return {*} The value of the parameter or an object
     *
     * @throws {Error} if the identifier is not defined
     */
    get(id) {
        this.assertIdExists(id);

        return typeof this.values[id] === 'function'
            ? this.values[id](this)
            : this.values[id];
    }

    /**
     * Gets a parameter or the closure defining an object.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @return {*} The value of the parameter or the closure defining an object
     *
     * @throws {Error} if the identifier is not defined
     */
    raw(id) {
        this.assertIdExists(id);

        return this.values[id];
    }

    /**
     * Sets a parameter or an object.
     *
     * Objects must be defined as Closures.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @param {*} value The value of the parameter or a closure to defined an object
     */
    set(id, value) {
        this.values[id] = value;
    }

    /**
     * Creates a closure that stores the result of the given closure for
     * uniqueness in the scope of this instance of Pimple.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @param {Function} fn A closure to wrap for uniqueness
     */
    share(id, fn) {
        const singletonFactory = () => {
            if (undefined === this.staticValues[id]) {
                this.staticValues[id] = fn(this);
            }

            return this.staticValues[id];
        };

        this.set(id, singletonFactory);
    }

    /**
     * Protects a function from being interpreted as a service.
     *
     * This is useful when you want to store a function as a parameter.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @param {Function} fn A closure to protect from being evaluated
     */
    protect(id, fn) {
        this.set(id, () => fn);
    }

    /**
     * Extends an object definition.
     *
     * Useful when you want to extend an existing object definition,
     * without necessarily loading that object.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @param {Function} fn A closure to extend the original
     *
     * @throws {Error} if the identifier is not defined or is not a closure
     */
    extend(id, fn) {
        this.assertIdRepresentsFunction(id);

        const factory = this.raw(id);

        this.set(id, () => fn(factory()));
    }

    /**
     * Checks if a parameter or an object is set.
     *
     * @param {string} id The unique identifier for the parameter or object
     * @return {boolean}
     */
    has(id) {
        return undefined !== this.values[id];
    }

    /**
     * Deletes a parameter or an object.
     *
     * @param {string} id The unique identifier for the parameter or object
     */
    ['delete'](id) {
        delete this.values[id];
        delete this.staticValues[id];
    }

    /**
     * Checks if a parameter or an object is set.
     *
     * @param {string} id The unique identifier for the parameter or object
     *
     * @throws {Error} if the identifier is not defined
     */
    assertIdExists(id) {
        if (!this.has(id)) {
            throw new Error(`Identifier ${id} is not defined.`);
        }
    }

    /**
     * Checks if a n identifier represents an object definition.
     *
     * @param {string} id The unique identifier for the parameter or object
     *
     * @throws {Error} if the identifier is not defined or is not a closure
     */
    assertIdRepresentsFunction(id) {
        if (typeof this.raw(id) !== 'function') {
            throw new Error(`Identifier ${id} does not contain an object definition.`);
        }
    }
}
